import React, { useEffect, useState, Children, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, EyeOff, Cookie, Palette, ArrowRight, BookOpen, Zap, Snowflake, Briefcase, Wheat, X, Clock, ShoppingBag } from 'lucide-react';
import { FaqAccordion } from '../components/FaqAccordion';
// Declare fbq for TypeScript
declare global {
  interface Window {
    fbq: (action: string, event: string, params?: Record<string, unknown>) => void;
  }
}
// Social proof notification data
const recentBuyers = [{
  name: 'María G.',
  location: 'Buenos Aires',
  time: 'hace 2 min'
}, {
  name: 'Carolina S.',
  location: 'Córdoba',
  time: 'hace 5 min'
}, {
  name: 'Luciana P.',
  location: 'Rosario',
  time: 'hace 8 min'
}, {
  name: 'Valentina R.',
  location: 'Mendoza',
  time: 'hace 12 min'
}, {
  name: 'Florencia M.',
  location: 'La Plata',
  time: 'hace 15 min'
}];
export function RecetasLanding() {
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  const [showNotification, setShowNotification] = useState(false);
  const [currentBuyer, setCurrentBuyer] = useState(0);
  // Initialize Meta Pixel and fire PageView
  useEffect(() => {
    // Load Meta Pixel script
    const script = document.createElement('script');
    script.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '33754024217546895');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);
  // Function to track AddToCart event
  const trackAddToCart = () => {
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'AddToCart', {
        content_name: 'Kit 3C - 250 Recetas para Niños',
        content_ids: ['47288850710691'],
        content_type: 'product',
        value: 12999,
        currency: 'ARS'
      });
    }
  };
  // Social proof notification cycle
  useEffect(() => {
    // Initial delay before first notification
    const initialDelay = setTimeout(() => {
      setShowNotification(true);
    }, 4000);
    return () => clearTimeout(initialDelay);
  }, []);
  useEffect(() => {
    if (showNotification) {
      // Hide notification after 5 seconds
      const hideTimer = setTimeout(() => {
        setShowNotification(false);
      }, 5000);
      // Show next notification after 8 seconds (3s hidden + 5s visible)
      const nextTimer = setTimeout(() => {
        setCurrentBuyer(prev => (prev + 1) % recentBuyers.length);
        setShowNotification(true);
      }, 12000);
      return () => {
        clearTimeout(hideTimer);
        clearTimeout(nextTimer);
      };
    }
  }, [showNotification, currentBuyer]);
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };
  const staggerContainer = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const faqItems = [{
    question: '¿Es solo un recetario?',
    answer: 'No. Son 5 mini-ebooks organizados por estrategia (Camuflar, Crujiente, Color, Favoritos, Diario). Cada uno resuelve un problema específico.'
  }, {
    question: '¿Incluye sin gluten?',
    answer: 'Sí. Todas las recetas SG están marcadas para identificarlas rápido.'
  }, {
    question: '¿Necesito ingredientes raros?',
    answer: 'No. Todo se consigue en el súper de siempre.'
  }, {
    question: '¿Cuánto tiempo me lleva?',
    answer: 'La mayoría se hacen en 20-30 minutos. Pensadas para la vida real.'
  }, {
    question: '¿Lo uso desde el celular?',
    answer: 'Sí. PDFs optimizados que abrís desde cualquier dispositivo.'
  }, {
    question: '¿Cómo empiezo hoy?',
    answer: 'Hacés clic, completás el pago, y recibís acceso inmediato por email.'
  }];
  return <div className="min-h-screen bg-[#FAF7F2] text-[#111111] overflow-x-hidden" data-id="element-130">
      {/* ALERT BANNER */}
      <AnimatePresence data-id="element-131">
        {showBanner && <motion.div initial={{
        y: -100,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: -100,
        opacity: 0
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="sticky top-0 z-50 bg-gradient-to-r from-[#0F766E] via-[#0D9488] to-[#0F766E] text-white py-2.5 px-4 shadow-lg" data-id="element-132">
            <div className="max-w-[1100px] mx-auto flex items-center justify-center gap-3 relative" data-id="element-133">
              <div className="flex items-center gap-2 text-center" data-id="element-134">
                <span className="animate-pulse" data-id="element-135">🔥</span>
                <p className="text-sm font-medium" data-id="element-136">
                  <span className="font-bold" data-id="element-137">
                    ¡Oferta por tiempo limitado!
                  </span>{' '}
                  48% OFF solo hoy —
                  <a href="#comprar" className="underline font-bold hover:text-orange-200 transition-colors" data-id="element-138">
                    Aprovechá ahora
                  </a>
                </p>
                <span className="animate-pulse" data-id="element-139">🔥</span>
              </div>
              <button onClick={() => setShowBanner(false)} className="absolute right-0 p-1 hover:bg-white/20 rounded-full transition-colors" aria-label="Cerrar banner" data-id="element-140">
                <X size={16} data-id="element-141" />
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* iPHONE-STYLE NOTIFICATION */}
      <AnimatePresence data-id="element-142">
        {showNotification && <motion.div initial={{
        x: -100,
        opacity: 0,
        scale: 0.8
      }} animate={{
        x: 0,
        opacity: 1,
        scale: 1
      }} exit={{
        x: -100,
        opacity: 0,
        scale: 0.8
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="fixed bottom-6 left-4 z-50 max-w-[320px]" data-id="element-143">
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/50 overflow-hidden" style={{
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)'
        }} data-id="element-144">
              {/* Notification Header - like iOS */}
              <div className="flex items-center gap-2 px-3 pt-3 pb-1" data-id="element-145">
                <div className="w-5 h-5 bg-[#0F766E] rounded-md flex items-center justify-center" data-id="element-146">
                  <ShoppingBag size={12} className="text-white" data-id="element-147" />
                </div>
                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide" data-id="element-148">
                  Vitalia Guides
                </span>
                <span className="text-[10px] text-gray-400 ml-auto" data-id="element-149">
                  {recentBuyers[currentBuyer].time}
                </span>
              </div>

              {/* Notification Content */}
              <div className="flex items-center gap-3 p-3 pt-1" data-id="element-150">
                {/* Product Image 1:1 */}
                <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 bg-gradient-to-br from-[#0F766E] to-[#0D5D58] flex items-center justify-center shadow-md" data-id="element-151">
                  <img src="https://cdn.magicpatterns.com/uploads/n88XmngPmNTWZZ7KPKJ1p1/shopify.png" alt="Kit 3C" className="w-full h-full object-cover" data-id="element-152" />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0" data-id="element-153">
                  <p className="text-sm font-semibold text-gray-900 truncate" data-id="element-154">
                    {recentBuyers[currentBuyer].name}
                  </p>
                  <p className="text-xs text-gray-500 truncate" data-id="element-155">
                    compró el Kit 3C
                  </p>
                  <p className="text-sm font-bold text-[#0F766E] mt-0.5" data-id="element-156">
                    ARS $12.999
                  </p>
                </div>
              </div>

              {/* Bottom indicator line - iOS style */}
              <div className="flex justify-center pb-2" data-id="element-157">
                <div className="w-10 h-1 bg-gray-300 rounded-full" data-id="element-158" />
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* HERO SECTION */}
      <section className="pt-8 pb-12 px-4 md:pt-12 md:pb-20 md:px-6" data-id="element-159">
        <div className="max-w-[600px] mx-auto" data-id="element-160">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} data-id="element-161">
            {/* Image container with overlaid title */}
            <div className="relative mb-6" data-id="element-162">
              {/* Title overlay - positioned at top of image */}
              <div className="absolute top-0 left-0 right-0 z-10 p-4 md:p-6 bg-gradient-to-b from-black/60 via-black/40 to-transparent" data-id="element-163">
                <motion.h1 variants={fadeIn} className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight leading-[1.15] text-white" data-id="element-164">
                  250 Recetas para Niños:{' '}
                  <span className="text-[#4ADE80]" data-id="element-165">Verduras Sin Peleas</span>
                </motion.h1>

                <motion.p variants={fadeIn} className="text-sm md:text-base text-white/90 mt-2 leading-relaxed" data-id="element-166">
                  El Kit 3C para que coman más verduras sin negociar y sin
                  cocinar 2 menús.
                </motion.p>
              </div>

              {/* Hero Image - 1:1 aspect ratio */}
              <motion.div initial={{
              opacity: 0,
              scale: 0.98
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.7,
              delay: 0.1
            }} className="relative aspect-square w-full overflow-hidden rounded-2xl shadow-xl" data-id="element-167">
                <img src="https://cdn.magicpatterns.com/uploads/2T4fpRFQpcaoVBxixU1P44/hero.png" alt="Mamá e hijo disfrutando comida saludable" className="w-full h-full object-cover" data-id="element-168" />
              </motion.div>
            </div>

            {/* Content below image */}
            <motion.ul variants={fadeIn} className="space-y-2.5 mb-6" data-id="element-169">
              {['Menos negociación en cada comida', 'Menos cocina doble (comida familiar)', 'Más aceptación usando el Sistema 3C', 'Recetas rápidas y realistas', 'Recetas SG (sin gluten) marcadas'].map((item, i) => <li key={i} className="flex items-center gap-2.5" data-id="element-170">
                  <div className="bg-[#0F766E]/15 p-1 rounded-full flex-shrink-0" data-id="element-171">
                    <Check size={12} className="text-[#0F766E] stroke-[3]" data-id="element-172" />
                  </div>
                  <span className="text-gray-700 font-medium text-sm" data-id="element-173">
                    {item}
                  </span>
                </li>)}
            </motion.ul>

            <motion.div variants={fadeIn} className="flex flex-col gap-3" data-id="element-174">
              <a href="#comprar" className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white px-7 py-3.5 rounded-xl font-semibold text-base transition-all hover:scale-[1.02] shadow-lg shadow-[#F97316]/30 flex items-center justify-center gap-2" data-id="element-175">
                Quiero el Kit 3C
                <ArrowRight size={18} data-id="element-176" />
              </a>
              <p className="text-xs text-gray-500 text-center" data-id="element-177">
                Acceso inmediato • Descarga digital • Actualizaciones incluidas
              </p>
              <a href="#que-incluye" className="text-[#0F766E] font-semibold hover:underline text-sm text-center" data-id="element-178">
                Ver qué incluye →
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ¿TE SUENA? */}
      <section className="py-10 md:py-16 bg-white" data-id="element-179">
        <div className="max-w-[900px] mx-auto px-4 md:px-6" data-id="element-180">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-2xl md:text-3xl font-bold text-center mb-8 text-[#111111]" data-id="element-181">
            ¿Te suena?
          </motion.h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4" data-id="element-182">
            {['Si ve algo verde, dice NO.', 'Cocino con amor… y no lo prueba.', 'Termino negociando "3 bocados más".', 'No tengo ideas y repito lo mismo.', 'No quiero cocinar 2 menús.', 'Me da culpa que no coma variado.'].map((text, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.05
          }} className="bg-[#FAF7F2] p-4 rounded-xl border border-gray-100 text-center" data-id="element-183">
                <p className="text-sm text-gray-700 font-medium leading-snug" data-id="element-184">
                  {text}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* MECHANISM - Sistema 3C */}
      <section className="py-12 md:py-20 bg-[#FAF7F2]" data-id="element-185">
        <div className="max-w-[900px] mx-auto px-4 md:px-6" data-id="element-186">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-10" data-id="element-187">
            <h2 className="text-2xl md:text-3xl font-bold text-[#111111] mb-2" data-id="element-188">
              Sistema 3C
            </h2>
            <p className="text-[#0F766E] font-medium text-sm" data-id="element-189">
              Camuflar • Crujiente • Color
            </p>
            <p className="text-gray-500 text-sm mt-3 max-w-md mx-auto" data-id="element-190">
              Bajá el rechazo antes del primer bocado con 3 palancas simples.
            </p>
          </motion.div>
          <div className="grid grid-cols-3 gap-3 md:gap-6" data-id="element-191">
            {[{
            icon: EyeOff,
            title: 'Camuflar',
            desc: 'Verduras adentro, sabor familiar',
            example: 'salsas, rellenos, mezclas uniformes',
            num: '01'
          }, {
            icon: Cookie,
            title: 'Crujiente',
            desc: 'Ganás por textura',
            example: 'dorado, rebozados, snacks crocantes',
            num: '02'
          }, {
            icon: Palette,
            title: 'Color',
            desc: 'Plato visualmente seguro',
            example: 'platos "seguros", uniformes y cerrados',
            num: '03'
          }].map((card, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-white p-4 md:p-6 rounded-xl border border-gray-100 text-center shadow-sm" data-id="element-192">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-[#0F766E] rounded-full flex items-center justify-center mx-auto mb-3 shadow-md shadow-[#0F766E]/20" data-id="element-193">
                  <card.icon size={22} className="text-white" data-id="element-194" />
                </div>
                <span className="text-[#F97316] font-bold text-lg mb-1 block" data-id="element-195">
                  {card.num}
                </span>
                <h3 className="font-bold text-[#111111] text-sm md:text-base mb-1" data-id="element-196">
                  {card.title}
                </h3>
                <p className="text-gray-500 text-xs mb-2" data-id="element-197">{card.desc}</p>
                <p className="text-[#0F766E] text-[10px] md:text-xs font-medium bg-[#0F766E]/5 px-2 py-1 rounded-full inline-block" data-id="element-198">
                  {card.example}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section id="que-incluye" className="py-12 md:py-20 bg-white" data-id="element-199">
        <div className="max-w-[1000px] mx-auto px-4 md:px-6" data-id="element-200">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-2xl md:text-3xl font-bold text-center mb-10 text-[#111111]" data-id="element-201">
            ¿Qué recibís hoy?
          </motion.h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-202">
            {[{
            title: 'Apagá la Guerra',
            subtitle: '(Camuflar)',
            badges: ['Express', 'Freezer'],
            gradient: 'from-orange-600 to-amber-700',
            bgImage: 'https://images.unsplash.com/photo-1570586437263-ab629fccc818?w=400&q=80'
          }, {
            title: 'Ganales por Textura',
            subtitle: '(Crujiente)',
            badges: ['Express', 'Vianda'],
            gradient: 'from-orange-500 to-red-600',
            bgImage: 'https://images.unsplash.com/photo-1447175008436-054170c2e979?w=400&q=80'
          }, {
            title: 'Que el Plato se Vea Seguro',
            subtitle: '(Color)',
            badges: ['Freezer', 'SG'],
            gradient: 'from-red-600 to-red-800',
            bgImage: 'https://images.unsplash.com/photo-1472476443507-c7a5948772fc?w=400&q=80'
          }, {
            title: 'Favoritos Versión Mejor',
            subtitle: '',
            badges: ['Express', 'Vianda'],
            gradient: 'from-green-600 to-green-800',
            bgImage: 'https://images.unsplash.com/photo-1459411552884-841db9b3cc2a?w=400&q=80'
          }, {
            title: 'Cole/Jardín + Desayunos y Meriendas',
            subtitle: '',
            badges: ['Vianda', 'SG'],
            gradient: 'from-teal-600 to-teal-800',
            bgImage: 'https://images.unsplash.com/photo-1544025162-d76694265947?w=400&q=80'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.08
          }} className={`relative rounded-xl overflow-hidden shadow-lg h-32 md:h-36 ${i === 4 ? 'sm:col-span-2 lg:col-span-1' : ''}`} data-id="element-203">
                {/* Background Image */}
                <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: `url(${item.bgImage})`
            }} data-id="element-204" />
                {/* Gradient Overlay */}
                <div className={`absolute inset-0 bg-gradient-to-r ${item.gradient} opacity-80`} data-id="element-205" />
                {/* Dark overlay for text readability */}
                <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/30 to-transparent" data-id="element-206" />

                {/* Content */}
                <div className="relative h-full flex flex-col justify-center p-4 md:p-5 text-white" data-id="element-207">
                  <h3 className="font-bold text-base md:text-lg leading-tight mb-1" data-id="element-208">
                    {item.title}
                    {item.subtitle && <span className="block text-white/90 font-medium text-sm mt-0.5" data-id="element-209">
                        {item.subtitle}
                      </span>}
                  </h3>
                  <p className="text-white/70 text-xs mb-2" data-id="element-210">50 recetas</p>
                  <div className="flex flex-wrap gap-1.5" data-id="element-211">
                    {item.badges.map((badge, j) => {
                  const badgeConfig: Record<string, {
                    icon: typeof Zap;
                    color: string;
                  }> = {
                    Express: {
                      icon: Zap,
                      color: 'bg-white/20 text-white'
                    },
                    Freezer: {
                      icon: Snowflake,
                      color: 'bg-white/20 text-white'
                    },
                    Vianda: {
                      icon: Briefcase,
                      color: 'bg-white/20 text-white'
                    },
                    SG: {
                      icon: Wheat,
                      color: 'bg-white/20 text-white'
                    }
                  };
                  const config = badgeConfig[badge];
                  return <span key={j} className={`${config.color} px-2 py-0.5 rounded text-[10px] md:text-xs font-medium flex items-center gap-1 backdrop-blur-sm`} data-id="element-212">
                          <config.icon size={10} data-id="element-213" />
                          {badge}
                        </span>;
                })}
                  </div>
                </div>
              </motion.div>)}
          </div>
          <p className="text-center text-xs text-gray-500 mt-6" data-id="element-214">
            Cada mini-ebook incluye recetas SG marcadas.
          </p>
        </div>
      </section>

      {/* PURCHASE SECTION - Product Image + Price Box */}
      <section id="comprar" className="py-12 md:py-20 bg-[#FAF7F2]" data-id="element-215">
        <div className="max-w-[900px] mx-auto px-4 md:px-6" data-id="element-216">
          {/* Product Image */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mb-10" data-id="element-217">
            <img src="https://cdn.magicpatterns.com/uploads/n88XmngPmNTWZZ7KPKJ1p1/shopify.png" alt="Kit 3C - 250 Recetas para Niños con los 5 mini-ebooks" className="w-full max-w-2xl mx-auto rounded-2xl shadow-xl" data-id="element-218" />
          </motion.div>

          {/* Price Box */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden max-w-md mx-auto" data-id="element-219">
            {/* Header */}
            <div className="bg-[#0F766E] text-white p-5 text-center" data-id="element-220">
              <h3 className="text-xl md:text-2xl font-bold mb-1" data-id="element-221">
                Kit 3C Completo
              </h3>
              <p className="text-sm opacity-90" data-id="element-222">250 Recetas + 5 Mini-Ebooks</p>
            </div>

            {/* Price Content */}
            <div className="p-6 text-center" data-id="element-223">
              {/* Original Price */}
              <div className="flex items-center justify-center gap-3 mb-2" data-id="element-224">
                <span className="text-gray-400 line-through text-lg" data-id="element-225">
                  ARS $24.999
                </span>
                <span className="bg-[#F97316] text-white text-xs font-bold px-2 py-1 rounded" data-id="element-226">
                  -48% OFF
                </span>
              </div>

              {/* Current Price */}
              <div className="mb-4" data-id="element-227">
                <span className="text-gray-500 text-sm" data-id="element-228">ARS </span>
                <span className="text-4xl md:text-5xl font-bold text-[#111111]" data-id="element-229">
                  $12.999
                </span>
              </div>

              <p className="text-gray-500 text-sm mb-6" data-id="element-230">
                Compra única • Acceso inmediato
              </p>

              {/* CTA Button */}
              <a href="https://vitaliaguides.com/cart/add?id=47288850710691&quantity=1" onClick={trackAddToCart} className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white py-4 px-6 rounded-xl font-bold text-lg transition-all hover:scale-[1.02] shadow-lg shadow-[#F97316]/20 flex items-center justify-center gap-3 mb-4" data-id="element-231">
                <span data-id="element-232">¡Sí, quiero el Kit 3C!</span>
                <ArrowRight size={20} data-id="element-233" />
              </a>

              {/* Trust badges */}
              <div className="flex items-center justify-center gap-4 text-xs text-gray-500 mb-6" data-id="element-234">
                <span className="flex items-center gap-1" data-id="element-235">
                  <Check size={14} className="text-[#0F766E]" data-id="element-236" />
                  Pago seguro
                </span>
                <span className="flex items-center gap-1" data-id="element-237">
                  <Check size={14} className="text-[#0F766E]" data-id="element-238" />
                  Acceso inmediato
                </span>
              </div>

              {/* Payment methods */}
              <div className="border-t border-gray-100 pt-4" data-id="element-239">
                <p className="text-xs text-gray-400 mb-3 flex items-center justify-center gap-1" data-id="element-240">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-241">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" data-id="element-242" />
                  </svg>
                  Pago seguro garantizado
                </p>
                <div className="flex items-center justify-center gap-3 opacity-60" data-id="element-243">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/5/5e/Visa_Inc._logo.svg" alt="Visa" className="h-6" data-id="element-244" />
                  <img src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" alt="Mastercard" className="h-6" data-id="element-245" />
                  <img src="https://upload.wikimedia.org/wikipedia/commons/b/b5/PayPal.svg" alt="PayPal" className="h-5" data-id="element-246" />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-10 md:py-16 bg-[#FAF7F2]" data-id="element-247">
        <div className="max-w-[600px] mx-auto px-4 md:px-6" data-id="element-248">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-xl md:text-2xl font-bold text-center mb-6 text-[#111111]" data-id="element-249">
            Preguntas comunes
          </motion.h2>
          <FaqAccordion items={faqItems} data-id="element-250" />
        </div>
      </section>

      {/* FINAL CTA */}

      {/* Footer */}
      <footer className="py-6 bg-white border-t border-gray-100 text-center px-4" data-id="element-251">
        <div className="flex items-center justify-center gap-4 mb-3" data-id="element-252">
          <button onClick={() => setShowTerms(true)} className="text-xs text-gray-500 hover:text-[#0F766E] transition-colors" data-id="element-253">
            Términos y Condiciones
          </button>
          <span className="text-gray-300" data-id="element-254">|</span>
          <button onClick={() => setShowPrivacy(true)} className="text-xs text-gray-500 hover:text-[#0F766E] transition-colors" data-id="element-255">
            Política de Privacidad
          </button>
        </div>
        <p className="text-gray-400 text-xs" data-id="element-256">
          © 2026 Vitalia Guides. Todos los derechos reservados.
        </p>
      </footer>

      {/* Terms Modal */}
      <AnimatePresence data-id="element-257">
        {showTerms && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowTerms(false)} data-id="element-258">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.2
        }} className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-259">
              <div className="flex items-center justify-between p-4 border-b border-gray-100" data-id="element-260">
                <h3 className="font-bold text-lg text-[#111111]" data-id="element-261">
                  Términos y Condiciones
                </h3>
                <button onClick={() => setShowTerms(false)} className="p-1 hover:bg-gray-100 rounded-full transition-colors" data-id="element-262">
                  <X size={20} className="text-gray-500" data-id="element-263" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[60vh] text-sm text-gray-600 leading-relaxed space-y-4" data-id="element-264">
                <p data-id="element-265">
                  <strong data-id="element-266">1. Aceptación de los Términos</strong>
                </p>
                <p data-id="element-267">
                  Al acceder y utilizar este sitio web y adquirir nuestros
                  productos digitales, aceptás estos términos y condiciones en
                  su totalidad.
                </p>

                <p data-id="element-268">
                  <strong data-id="element-269">2. Productos Digitales</strong>
                </p>
                <p data-id="element-270">
                  Los productos ofrecidos son ebooks digitales en formato PDF.
                  Una vez realizada la compra, recibirás acceso inmediato a
                  través del correo electrónico proporcionado.
                </p>

                <p data-id="element-271">
                  <strong data-id="element-272">3. Uso Personal</strong>
                </p>
                <p data-id="element-273">
                  Los productos adquiridos son para uso personal y no pueden ser
                  redistribuidos, revendidos o compartidos sin autorización
                  expresa.
                </p>

                <p data-id="element-274">
                  <strong data-id="element-275">4. Propiedad Intelectual</strong>
                </p>
                <p data-id="element-276">
                  Todo el contenido, incluyendo textos, imágenes y diseños, es
                  propiedad de Vitalia Guides y está protegido por las leyes de
                  propiedad intelectual.
                </p>

                <p data-id="element-277">
                  <strong data-id="element-278">5. Política de Reembolso</strong>
                </p>
                <p data-id="element-279">
                  Debido a la naturaleza digital de los productos, no se aceptan
                  devoluciones una vez entregado el acceso al contenido.
                </p>

                <p data-id="element-280">
                  <strong data-id="element-281">6. Limitación de Responsabilidad</strong>
                </p>
                <p data-id="element-282">
                  El contenido es educativo y no reemplaza el consejo de
                  profesionales de la salud o nutrición.
                </p>

                <p data-id="element-283">
                  <strong data-id="element-284">7. Modificaciones</strong>
                </p>
                <p data-id="element-285">
                  Nos reservamos el derecho de modificar estos términos en
                  cualquier momento. Los cambios serán efectivos inmediatamente
                  después de su publicación.
                </p>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Privacy Modal */}
      <AnimatePresence data-id="element-286">
        {showPrivacy && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowPrivacy(false)} data-id="element-287">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.2
        }} className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-288">
              <div className="flex items-center justify-between p-4 border-b border-gray-100" data-id="element-289">
                <h3 className="font-bold text-lg text-[#111111]" data-id="element-290">
                  Política de Privacidad
                </h3>
                <button onClick={() => setShowPrivacy(false)} className="p-1 hover:bg-gray-100 rounded-full transition-colors" data-id="element-291">
                  <X size={20} className="text-gray-500" data-id="element-292" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[60vh] text-sm text-gray-600 leading-relaxed space-y-4" data-id="element-293">
                <p data-id="element-294">
                  <strong data-id="element-295">1. Información que Recopilamos</strong>
                </p>
                <p data-id="element-296">
                  Recopilamos información personal que nos proporcionás
                  voluntariamente, como nombre y correo electrónico, al realizar
                  una compra.
                </p>

                <p data-id="element-297">
                  <strong data-id="element-298">2. Uso de la Información</strong>
                </p>
                <p data-id="element-299">
                  Utilizamos tu información para: procesar tu compra, enviarte
                  los productos digitales, comunicarnos contigo sobre
                  actualizaciones y mejorar nuestros servicios.
                </p>

                <p data-id="element-300">
                  <strong data-id="element-301">3. Protección de Datos</strong>
                </p>
                <p data-id="element-302">
                  Implementamos medidas de seguridad para proteger tu
                  información personal contra acceso no autorizado o alteración.
                </p>

                <p data-id="element-303">
                  <strong data-id="element-304">4. Compartir Información</strong>
                </p>
                <p data-id="element-305">
                  No vendemos ni compartimos tu información personal con
                  terceros, excepto cuando sea necesario para procesar pagos o
                  cumplir con obligaciones legales.
                </p>

                <p data-id="element-306">
                  <strong data-id="element-307">5. Cookies</strong>
                </p>
                <p data-id="element-308">
                  Utilizamos cookies para mejorar tu experiencia de navegación y
                  analizar el tráfico del sitio.
                </p>

                <p data-id="element-309">
                  <strong data-id="element-310">6. Tus Derechos</strong>
                </p>
                <p data-id="element-311">
                  Tenés derecho a acceder, corregir o eliminar tu información
                  personal. Contactanos para ejercer estos derechos.
                </p>

                <p data-id="element-312">
                  <strong data-id="element-313">7. Contacto</strong>
                </p>
                <p data-id="element-314">
                  Para consultas sobre privacidad, podés contactarnos a través
                  de nuestros canales oficiales.
                </p>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}